import { apiGet, apiPost } from './utils';
import type { FHRequest, FHRequestMessage, PagedResponse, PendingResponse, RequestDelay } from '../types';

export interface CreateRequestParams {
  content: string;
  title?: string;
  project?: string;
  [key: string]: unknown;
}

export interface CreateMessageParams {
  request: string;
  content: string;
  [key: string]: unknown;
}

export interface ClientInfo {
  uuid: string;
  key: string | null;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  phone_number: string | null;
  timezone_city: string | null;
  date_created: string | null;
  home_city: string | null;
  home_state: string | null;
  home_zip: string | null;
  work_city: string | null;
  work_state: string | null;
}

export interface MentorHistoryItem {
  uuid: string;
  associated_type: string;
  user_name: string | null;
  user_uuid: string | null;
  date_created: string;
}

export interface MentorHistoryResponse {
  items: MentorHistoryItem[];
}

export interface RequestCreditItem {
  key: string;
  uuid: string;
  title: string | null;
  date_created: string | null;
  task_credits: number;
  status: number;
  solved: boolean;
}

export interface RequestCreditsResponse {
  items: RequestCreditItem[];
  cursor: string | null;
  has_more: boolean;
  // Only on the first page
  tasks_remaining?: number;
  tasks_expire?: string | null;
}

/** Legacy FHUserRequestDecline reason codes an assistant can pick. */
export const DECLINE_REASONS = [
  { value: 1, label: 'Not qualified for this' },
  { value: 2, label: 'Too big / time consuming' },
  { value: 3, label: 'Not worth the money' },
] as const;

export interface RequestDecline {
  uuid: string;
  key: string;
  user: string;
  user_name: string;
  request: string;
  request_key: string | null;
  request_title: string | null;
  reason: number;
  reason_label: string;
  date_created: string;
}

export const requestApi = {
  /**
   * Create a new request
   */
  async create(params: CreateRequestParams): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/create', params);
  },

  /**
   * Get a single request by key
   */
  async get(requestKey: string): Promise<FHRequest> {
    return apiGet<FHRequest>('/request/get', { request: requestKey });
  },

  /**
   * List the current user's requests with the task credits each one used
   */
  async listCredits(cursor?: string): Promise<RequestCreditsResponse> {
    return apiGet<RequestCreditsResponse>('/request/list/credits', { cursor });
  },

  /**
   * List requests for the current user
   */
  async listByUser(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/by-user', { cursor });
  },

  /**
   * List the current user's archived requests
   */
  async listArchived(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/archived', { cursor });
  },

  /**
   * Archive (or unarchive) one of the current user's requests.
   * Only hides it from their default list; status is unchanged.
   */
  async setClientArchived(requestKey: string, archived: boolean): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/archive', { request: requestKey, archived });
  },

  /**
   * List requests for a specific project
   */
  async listByProject(
    project: string,
    cursor?: string
  ): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/by-project', {
      project,
      cursor,
    });
  },

  /**
   * List messages for a request
   */
  async listMessages(
    requestKey: string,
    cursor?: string
  ): Promise<PagedResponse<FHRequestMessage>> {
    return apiGet<PagedResponse<FHRequestMessage>>('/request/message/list', {
      request: requestKey,
      cursor,
    });
  },

  /**
   * Create a message on a request
   */
  async createMessage(params: CreateMessageParams): Promise<FHRequestMessage> {
    return apiPost<FHRequestMessage>('/request/message/create', params);
  },

  /**
   * Submit a response for mentor approval.
   * The response is NOT visible to the client until a mentor approves it.
   * @param requestKey - The request to respond to
   * @param content - The response content
   * @param suggestedStatus - What status to set after mentor approval
   */
  async submitResponse(
    requestKey: string,
    content: string,
    suggestedStatus: number,
    delay?: string
  ): Promise<{ response: unknown; request: FHRequest }> {
    return apiPost('/request/submit-response', {
      request: requestKey,
      content,
      suggested_status: suggestedStatus,
      ...(delay && { delay }),
    });
  },

  /**
   * Update request status
   * @param requestKey - The request to update
   * @param status - The new status (usually STATUS_AWAITING_MENTOR_APPROVAL for submissions)
   * @param suggestedStatus - Optional: what status to use after mentor approval
   */
  async updateStatus(
    requestKey: string,
    status: number,
    suggestedStatus?: number
  ): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/update-status', {
      request: requestKey,
      status,
      suggested_status: suggestedStatus,
    });
  },

  /**
   * Get client info for a request (assistant use)
   */
  async getClientInfo(requestKey: string): Promise<ClientInfo> {
    return apiGet<ClientInfo>('/request/client-info', { request: requestKey });
  },

  /**
   * Get the pending response for a request the current user submitted.
   * Used to display the response content while awaiting mentor approval.
   * @param requestKey - The request key
   */
  async getPendingResponse(requestKey: string): Promise<PendingResponse> {
    return apiGet<PendingResponse>(`/request/pending-response/${requestKey}`);
  },

  /**
   * Request help from a manager for a request.
   * Sets the needs_manager_attention flag and optionally creates an internal note.
   * @param requestKey - The request to flag for help
   * @param message - Optional message explaining what help is needed
   */
  async requestHelp(requestKey: string, message?: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/help', {
      request: requestKey,
      ...(message && { message }),
    });
  },

  /**
   * Cancel a help request - assistant clears the attention flag themselves.
   * @param requestKey - The request to cancel help for
   */
  async cancelHelp(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/help/cancel', {
      request: requestKey,
    });
  },

  /**
   * Close a task without sending a response to the client.
   * Submits to the mentor queue for approval. When approved, the task
   * closes silently without notifying the client.
   * @param requestKey - The request to close
   */
  async closeTask(requestKey: string): Promise<{ response: unknown; request: FHRequest }> {
    return apiPost('/request/submit-response', {
      request: requestKey,
      suggested_status: 20, // STATUS_CLOSED
      close_without_response: true,
    });
  },

  /**
   * Get mentor action history for a request.
   * Returns claim, approve, deny, unclaim actions by mentors.
   * @param requestKey - The request to get history for
   */
  async getMentorHistory(requestKey: string): Promise<MentorHistoryResponse> {
    return apiGet<MentorHistoryResponse>('/request/mentor-history', { request: requestKey });
  },

  /**
   * Get the active delay for a request (scheduled or fired).
   * Returns null if no active delay exists.
   */
  async getActiveDelay(requestUuid: string): Promise<RequestDelay | null> {
    const result = await apiGet<RequestDelay>('/request-delay/active', { request: requestUuid });
    // Backend returns empty object if no active delay
    if (!result || !result.uuid) return null;
    return result;
  },

  /**
   * Get all delays for a request (for timeline display).
   */
  async getDelayHistory(requestUuid: string): Promise<RequestDelay[]> {
    const result = await apiGet<{ items: RequestDelay[] }>('/request-delay/history', { request: requestUuid });
    return result.items || [];
  },

  /**
   * Rate a completed request (client only, 1-5 stars).
   * @param requestKey - The request to rate
   * @param rating - Star rating 1-5
   */
  async rateRequest(requestKey: string, rating: number): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/rate', { request: requestKey, rating });
  },

  /**
   * List available (unclaimed) requests for the current assistant
   */
  async listAvailable(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/available', { cursor });
  },

  /**
   * Hide a task from the current assistant's Available list, with a reason
   */
  async decline(requestKey: string, reason: number): Promise<RequestDecline> {
    return apiPost<RequestDecline>('/request/decline', { request: requestKey, reason });
  },

  /**
   * Put a hidden task back on the current assistant's Available list
   */
  async undoDecline(requestKey: string): Promise<{ request: string; declined: boolean }> {
    return apiPost('/request/decline/undo', { request: requestKey });
  },

  /**
   * Every decline on one request, newest first (managers)
   */
  async listDeclines(requestKey: string): Promise<{ items: RequestDecline[] }> {
    return apiGet<{ items: RequestDecline[] }>('/request/decline/list', { request: requestKey });
  },

  /**
   * Recent declines across all requests (managers)
   */
  async listRecentDeclines(cursor?: string): Promise<PagedResponse<RequestDecline>> {
    return apiGet<PagedResponse<RequestDecline>>('/request/decline/recent', { cursor });
  },

  /**
   * List requests claimed by the current assistant
   */
  async listClaimed(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/claimed', { cursor });
  },

  /**
   * List requests closed by the current assistant
   */
  async listClosed(cursor?: string): Promise<PagedResponse<FHRequest>> {
    return apiGet<PagedResponse<FHRequest>>('/request/list/closed', { cursor });
  },

  /**
   * Claim a request for the current assistant
   */
  async claim(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/claim', { request: requestKey });
  },

  /**
   * Release (unclaim) a request the current assistant claimed, returning it
   * to the available queue
   */
  async release(requestKey: string): Promise<FHRequest> {
    return apiPost<FHRequest>('/request/release', { request: requestKey });
  },
};
